/**
 * Parameter Template Management
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, PlayArrow as ApplyIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ParameterTemplate, ParameterType } from '../../types/parameters';

const TemplateManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [_selectedTemplate, setSelectedTemplate] = useState<ParameterTemplate | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    param_type: ParameterType.STORE,
  });

  // Fetch templates
  const { data: templates } = useQuery<ParameterTemplate[]>({
    queryKey: ['parameterTemplates'],
    queryFn: async () => {
      const response = await api.getParameterTemplates();
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.createParameterTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameterTemplates'] });
      setDialogOpen(false);
      setSuccessMessage('Template created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (templateId: string) => api.deleteParameterTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameterTemplates'] });
      setSuccessMessage('Template deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const handleCreate = () => {
    const templateData = {
      template_name: formData.template_name,
      description: formData.description,
      param_type: formData.param_type,
      template_params: [
        {
          param_name: 'coverage_days',
          param_value: { coverage_days: 14 },
          description: 'Default coverage days',
        },
      ],
      is_default: false,
    };

    createMutation.mutate(templateData);
  };

  const handleApply = (template: ParameterTemplate) => {
    setSelectedTemplate(template);
    setApplyDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Parameter Templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Create Template
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Template Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Parameters</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates?.map((template) => (
              <TableRow key={template.template_id}>
                <TableCell>{template.template_name}</TableCell>
                <TableCell><Chip label={template.param_type} size="small" /></TableCell>
                <TableCell>{template.description || '-'}</TableCell>
                <TableCell>{template.template_params.length} params</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleApply(template)} color="primary">
                    <ApplyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deleteMutation.mutate(template.template_id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )) || (
              <TableRow>
                <TableCell colSpan={5} align="center">No templates found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Parameter Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.template_name}
            onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Template Type</InputLabel>
            <Select
              value={formData.param_type}
              label="Template Type"
              onChange={(e) => setFormData({ ...formData, param_type: e.target.value as ParameterType })}
            >
              <MenuItem value={ParameterType.STORE}>Store Parameters</MenuItem>
              <MenuItem value={ParameterType.PRODUCT}>Product Parameters</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)}>
        <DialogTitle>Apply Template</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2 }}>
            Template application functionality will be available soon. You can apply templates to multiple entities at once.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManagement;
